import { cn } from 'cn';
import { type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionsRow } from '@vestigas/ui/molecules';

import { useFormContext } from './form-context';

type FormActionsRowProps = Partial<ComponentProps<typeof ActionsRow>> & {
  readonly hasResetButton?: boolean;
  readonly requireDirty?: boolean;
};

/**
 * TanStack Form wrapper for the ActionsRow component.
 * Typically handles submitting the form, resetting the form, and canceling editing.
 * @example
 *   <form.AppForm>
 *     <form.ActionsRow onCancel={handleCancel} />
 *   </form.AppForm>
 */
export const FormActionsRow = ({
  children,
  className,
  hasResetButton = true,
  onCancel,
  primaryAction,
  requireDirty = true,
  secondaryAction,
  shouldBeHidden,
  style,
  tertiaryAction,
}: FormActionsRowProps) => {
  const { t } = useTranslation('forms');

  const form = useFormContext();

  return (
    <form.Subscribe
      selector={(state) => [state.canSubmit, state.isDirty, state.isSubmitting]}
    >
      {([canSubmit, isDirty, isSubmitting]) => (
        <ActionsRow
          className={className}
          primaryAction={{
            disabled: !canSubmit || (requireDirty && !isDirty) || isSubmitting,
            inProgress: isSubmitting,
            onClick() {
              void form.handleSubmit();
            },
            ...primaryAction,
          }}
          secondaryAction={secondaryAction}
          shouldBeHidden={shouldBeHidden}
          style={style}
          tertiaryAction={
            hasResetButton
              ? {
                  buttonStyle: 'link',
                  className: cn(
                    'text-tiga-action-default transition-opacity duration-400',
                    { 'flex-initial opacity-0': !isDirty || isSubmitting },
                  ),
                  onClick() {
                    form.reset();
                  },
                  text: t(($) => $.resetButton.text),
                  variant: 'text',
                  ...tertiaryAction,
                }
              : tertiaryAction
          }
          onCancel={
            onCancel
              ? () => {
                  onCancel();
                  form.reset();
                }
              : undefined
          }
        >
          {children}
        </ActionsRow>
      )}
    </form.Subscribe>
  );
};
